import React, { useState, useCallback, useRef } from 'react';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { UploadIcon } from '../icons/UploadIcon';
import { DeleteIcon } from '../icons/DeleteIcon';
import { AudioRecorder } from '../shared/AudioRecorder';
import { generateScriptFromAudio, generateScriptFromConcept, generateScriptFromVideo } from '../../services/geminiService';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { CloseIcon } from '../icons/CloseIcon';
import { CREDIT_COSTS } from '../../types';
import { ConceptGenerator } from './ConceptGenerator';
import { IndustryVideoTypeCastPicker, resolvePickerForPrompt } from './IndustryVideoTypeCastPicker';
import { BrandScopePicker } from './BrandScopePicker';
import { suggestIndustryFromBrand } from './conceptCatalog';
import { Sparkles, FileText, Lightbulb, Mic, ArrowRight, CheckCircle, Pencil } from 'lucide-react';
import { resolveShotDurationSeconds } from '../../utils/videoModelDurations';

export const AudioStyleModal = ({ initialVideoStyle = 'realistic', initialVideoPace = 'standard', onConfirm, onClose }) => {
    const [selectedStyle, setSelectedStyle] = useState('dialogue');
    const [region, setRegion] = useState('Global');
    const [language, setLanguage] = useState('English');
    // Video style — controls the LLM's visual direction for the script's
    // characters, props and environments. Persisted on the project so
    // downstream Cast, Storyboard, and Pre-Production prompts can reuse it.
    const [videoStyle, setVideoStyle] = useState(initialVideoStyle);
    // Feb 2026 — Video pace controls how many beats/shots the LLM packs into
    // the target duration. "Slow" → fewer, longer contemplative shots.
    // "Fast" → more, quick-cut shots. "Standard" is the LLM's default cadence.
    const [videoPace, setVideoPace] = useState(initialVideoPace);

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#181820] border border-gray-700 rounded-xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="script-options-close-btn">
                    <CloseIcon className="w-5 h-5" />
                </button>
                <h3 className="text-xl font-bold mb-4 text-center">Script Generation Options</h3>
                
                <div className="space-y-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Target Region / Country</label>
                        <input
                            type="text"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            placeholder="e.g., USA, India, Europe, Global"
                            className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                            data-testid="script-region-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Dialogue/Narration Language</label>
                        <input
                            type="text"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            placeholder="e.g., English, Spanish, Hindi"
                            className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                            data-testid="script-language-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Script structure/descriptions will remain in English.</p>
                    </div>
                </div>

                <p className="text-sm font-medium text-gray-300 mb-2">Video Style</p>
                <div className="grid grid-cols-2 gap-2 mb-6">
                    {[
                        { id: 'realistic',         label: 'Realistic',         desc: 'Photorealistic characters, props and world.' },
                        { id: 'cartoon_animation', label: 'Cartoon Animation', desc: '2D cartoon characters and stylised world.' },
                        { id: 'pixar_3d',          label: 'Pixar 3D',          desc: 'Pixar-style 3D characters and world.' },
                        { id: 'anime',             label: 'Anime',             desc: 'Anime-style characters and world.' },
                    ].map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => setVideoStyle(option.id)}
                            data-testid={`video-style-option-${option.id}`}
                            className={`p-3 rounded-lg text-left transition-all border ${
                                videoStyle === option.id
                                    ? 'bg-purple-600/20 border-purple-500 ring-1 ring-purple-500'
                                    : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                            }`}
                        >
                            <div className="font-semibold text-white text-sm">{option.label}</div>
                            <div className="text-[11px] text-gray-400 mt-0.5 leading-snug">{option.desc}</div>
                        </button>
                    ))}
                </div>

                <p className="text-sm font-medium text-gray-300 mb-2">Choose Video Pace</p>
                <div className="grid grid-cols-3 gap-2 mb-6">
                    {[
                        { id: 'slow',     label: 'Slow',     desc: 'Fewer, longer contemplative shots.' },
                        { id: 'standard', label: 'Standard', desc: 'Balanced cadence for most stories.' },
                        { id: 'fast',     label: 'Fast',     desc: 'More, quick-cut energetic shots.' },
                    ].map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => setVideoPace(option.id)}
                            data-testid={`video-pace-option-${option.id}`}
                            className={`p-3 rounded-lg text-left transition-all border ${
                                videoPace === option.id
                                    ? 'bg-purple-600/20 border-purple-500 ring-1 ring-purple-500'
                                    : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                            }`}
                        >
                            <div className="font-semibold text-white text-sm">{option.label}</div>
                            <div className="text-[11px] text-gray-400 mt-0.5 leading-snug">{option.desc}</div>
                        </button>
                    ))}
                </div>

                <p className="text-sm font-medium text-gray-300 mb-2">Audio Style</p>
                <div className="space-y-3 mb-6">
                    {[
                        { id: 'dialogue', label: 'Dialogue', desc: 'Focus on character conversations.' },
                        { id: 'narration', label: 'Narration', desc: 'Focus on voiceover storytelling.' },
                        { id: 'both', label: 'Both', desc: 'Mix of dialogue and narration.' },
                        { id: 'asmr', label: 'ASMR', desc: 'ASMR audio only — no dialogue, narration, SFX, BGM, or ambient sound.' },
                        { id: 'none', label: 'None', desc: 'Visuals only. Music & SFX.' },
                    ].map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => setSelectedStyle(option.id)}
                            data-testid={`audio-style-option-${option.id}`}
                            className={`w-full p-3 rounded-lg text-left transition-all border ${
                                selectedStyle === option.id 
                                    ? 'bg-purple-600/20 border-purple-500 ring-1 ring-purple-500' 
                                    : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                            }`}
                        >
                            <div className="font-semibold text-white">{option.label}</div>
                            <div className="text-xs text-gray-400">{option.desc}</div>
                        </button>
                    ))}
                </div>

                <button
                    onClick={() => onConfirm(selectedStyle, region, language, videoStyle, videoPace)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition-colors"
                    data-testid="script-options-generate-btn"
                >
                    Generate Script
                </button>
            </div>
        </div>
    );
};

export const ScriptInput = ({ initialData, onSubmit, onBack, error: externalError, onDeductCredits, onBrandScopeApply }) => {
    const [script, setScript] = useState(initialData.script || '');
    // Default to "I'm not sure — give me ideas" for new projects (most users
    // are first-timers and benefit from the guided concept generator). If the
    // user already has a script, jump them straight to text mode.
    const [mode, setMode] = useState(initialData.script ? 'text' : 'generateConcept');
    const [audioMode, setAudioMode] = useState('upload');
    const [audioFile, setAudioFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const audioFileInputRef = useRef(null);
    const [concept, setConcept] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [brandName, setBrandName] = useState(initialData.brandName || '');
    // Brand scope (Feb 2026): populated when the user picks a saved brand
    // from BrandScopePicker. Persisted onto activeProject so downstream AI
    // calls carry the same scope. When set, the manual Brand Name / Reference
    // block inside ConceptGenerator is hidden (the saved brand replaces it).
    const [brandScope, setBrandScope] = useState({
        brandId:    initialData.brandId    || null,
        productIds: initialData.productIds || [],
        serviceIds: initialData.serviceIds || [],
    });
    const [castMode, setCastMode] = useState(initialData.castMode || 'with_actors');
    // Industry / Video-Type / Cast picker state shared with the "rough idea"
    // path (Feb 2026). The picker is also rendered inline by ConceptGenerator
    // for the "I'm not sure" path, but for "I have a rough idea" we expose
    // the SAME steps directly so the LLM gets industry + video-type context
    // when turning the user's 1-2 line idea into a full script.
    const [pickerValue, setPickerValue] = useState({
        industryId: initialData.industryId || null,
        videoTypeId: initialData.videoTypeId || null,
        customVideoTypeName: initialData.customVideoTypeName || '',
        customIndustryName: initialData.customIndustryName || '',
        castMode: initialData.castMode || 'with_actors',
    });
    // Audio style chosen in the Script Generation Options modal ('dialogue',
    // 'narration', 'both', 'asmr', 'none'). Persisted onto the project on
    // submit so downstream video generation (Storyboard, PreProduction,
    // videoPayloadBuilder) can honour it.
    const [audioStyle, setAudioStyle] = useState(initialData.audioStyle || 'both');
    // Visual style chosen in the same modal ('realistic', 'cartoon_animation',
    // 'pixar_3d', 'anime'). Carried into the project so Cast & World, image
    // generation, and pre-production prompts can stay visually consistent.
    const [videoStyle, setVideoStyle] = useState(initialData.videoStyle || 'realistic');
    // Feb 2026 — Video pace controls the LLM's shot cadence when generating
    // the script: 'slow' fewer/longer beats, 'standard' balanced, 'fast'
    // quick-cut. Persisted on the project for downstream reuse.
    const [videoPace, setVideoPace] = useState(initialData.videoPace || 'standard');
    
    const [recreateMode, setRecreateMode] = useState('url');
    const [videoUrl, setVideoUrl] = useState('');
    const [videoFile, setVideoFile] = useState(null);
    const videoFileInputRef = useRef(null);

    const [modInstructionMode, setModInstructionMode] = useState('text');
    const [modInstructionText, setModInstructionText] = useState('');
    const [modInstructionAudio, setModInstructionAudio] = useState(null);
    const [modInstructionAudioMode, setModInstructionAudioMode] = useState('upload');
    const modAudioFileInputRef = useRef(null);

    const [showStyleModal, setShowStyleModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const handleAudioFile = (file) => {
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAudioFile({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
            };
            reader.readAsDataURL(file);
        } else {
            setGenerationError("Invalid file type. Please upload an audio file.");
        }
    };
    
    const handleAudioFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleAudioFile(file);
        }
    };

    const handleDrop = useCallback((e, fileHandler) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            fileHandler(file);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);
    
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);
    
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const triggerGenerateScript = () => {
        if (!concept?.trim() || isGenerating) return;
        setPendingAction('concept');
        setShowStyleModal(true);
    };

    const triggerCreateScriptFromVideo = () => {
        if ((recreateMode === 'url' && !videoUrl?.trim()) || (recreateMode === 'upload' && !videoFile)) {
            return;
        }
        setPendingAction('recreate');
        setShowStyleModal(true);
    };

    const triggerGenerateScriptFromAudio = () => {
        if (!audioFile) return;
        setPendingAction('audio');
        setShowStyleModal(true);
    };

    const handleStyleConfirm = async (style, region, language, vStyle, vPace) => {
        setShowStyleModal(false);
        setIsGenerating(true);
        setGenerationError(null);
        setAudioStyle(style);
        if (vStyle) setVideoStyle(vStyle);
        if (vPace) setVideoPace(vPace);

        // Extract project details from initialData
        const projectDetails = {
            name: initialData.name || '',
            duration: initialData.duration || '1-3 minutes',
            aspectRatio: initialData.aspectRatio || '16:9',
            audioStyle: style,  // "dialogue", "narration", "both", or "none"
            videoStyle: vStyle || videoStyle,  // "realistic", "cartoon_animation", "pixar_3d", "anime"
            videoPace: vPace || videoPace,     // "slow" | "standard" | "fast"
        };

        // For the "I have a rough idea" path, fold the user's Industry /
        // Video Type / Cast selections into the script-generation request
        // so the LLM produces a script that's already targeted at the right
        // industry, format, and on-screen-people policy — instead of a
        // generic ad script that ignores those choices.
        if (pendingAction === 'concept') {
            const pick = resolvePickerForPrompt(pickerValue);
            if (pick) {
                projectDetails.industry  = pick.industry;
                projectDetails.videoType = pick.videoType;
                projectDetails.castMode  = pick.castMode;
                // Mirror onto top-level so existing code paths see it too.
                setCastMode(pick.castMode);
            }
        }

        try {
            let generatedScript = '';

            if (pendingAction === 'concept') {
                // Credits are deducted by backend - removed upfront deduction
                const result = await generateScriptFromConcept(concept, style, region, language, projectDetails);
                generatedScript = result?.script || result;
            } else if (pendingAction === 'audio' && audioFile) {
                // Credits are deducted by backend - removed upfront deduction
                const result = await generateScriptFromAudio(audioFile.data, style, region, language, projectDetails);
                generatedScript = result?.script || result;
            } else if (pendingAction === 'recreate') {
                let videoInputData;

                if (recreateMode === 'upload' && videoFile) {
                    videoInputData = videoFile;
                } else if (recreateMode === 'url' && videoUrl) {
                    try {
                        setGenerationError("Fetching video from URL... This may take a moment.");
                        const response = await fetch(videoUrl);
                        if (!response.ok) throw new Error(`Failed to fetch video from URL: ${response.statusText}`);
                        const blob = await response.blob();
                        const mimeType = blob.type;
                        if (!mimeType.startsWith('video/')) throw new Error('The provided URL does not point to a valid video file.');
                        const dataUrl = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        });
                        videoInputData = { data: dataUrl, mimeType };
                    } catch (fetchError) {
                        console.error(fetchError);
                        throw new Error("Could not fetch video from URL. Please try uploading the video directly.");
                    }
                }
                
                setGenerationError(null); 

                const instructions = {};
                if (modInstructionText?.trim()) instructions.text = modInstructionText;
                if (modInstructionAudio) instructions.audio = modInstructionAudio;

                // Credits are deducted by backend - removed upfront deduction
                
                generatedScript = await generateScriptFromVideo(
                    videoInputData,
                    Object.keys(instructions).length > 0 ? instructions : undefined,
                    style,
                    region,
                    language,
                    projectDetails
                );
                // Extract script from result object if wrapped
                if (generatedScript && typeof generatedScript === 'object') {
                    generatedScript = generatedScript.script || generatedScript;
                }
            }

            if (generatedScript) {
                setScript(generatedScript);
                setMode('text');
                // Auto-advance to "Cast & World" instead of stopping at the
                // intermediate "Generate Characters" review screen.
                // Requirement (Feb 2026): reduce unnecessary clicks — when
                // the script has been auto-generated by the AI we already
                // have all the context the wizard needs (brand, industry,
                // video-type, cast, audio/video style). Send it straight
                // through with the same `extra` payload that `handleSubmit`
                // assembles, so downstream stages have the full picker
                // context.
                const extra = {};
                if (brandName.trim()) extra.brandName = brandName.trim();
                if (audioStyle) extra.audioStyle = audioStyle;
                if (videoStyle) extra.videoStyle = videoStyle;
                if (videoPace)  extra.videoPace  = videoPace;
                if (castMode) extra.castMode = castMode;
                if (pickerValue.industryId)            extra.industryId          = pickerValue.industryId;
                if (pickerValue.videoTypeId)           extra.videoTypeId         = pickerValue.videoTypeId;
                if (pickerValue.customVideoTypeName)   extra.customVideoTypeName = pickerValue.customVideoTypeName;
                if (pickerValue.customIndustryName)    extra.customIndustryName  = pickerValue.customIndustryName;
                if (pickerValue.castMode)              extra.castMode            = pickerValue.castMode;
                // The auto-advance is intentionally synchronous-ish — the
                // wizard parent (`App.js`) reads `script` straight from the
                // `data` argument we pass here, so we don't need to wait
                // for React state to flush.
                onSubmit({ script: generatedScript, ...extra });
            }

        } catch (err) {
            const message = err instanceof Error ? err.message : "An unknown error occurred during script generation.";
            setGenerationError(message);
        } finally {
            setIsGenerating(false);
            setPendingAction(null);
        }
    };

    const handleVideoFile = (file) => {
        if (file && file.type.startsWith('video/')) {
            const MAX_SIZE_MB = 50;
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                setGenerationError(`Video file is too large. Please select a file smaller than ${MAX_SIZE_MB}MB.`);
                return;
            }
            setGenerationError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setVideoFile({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
                setVideoUrl('');
            };
            reader.readAsDataURL(file);
        } else {
            setGenerationError("Invalid file type. Please upload a video file.");
        }
    };

    const handleVideoFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleVideoFile(file);
        }
    };

    const handleModAudioFile = (file) => {
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setModInstructionAudio({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
            };
            reader.readAsDataURL(file);
        } else {
            setGenerationError("Invalid file type. Please upload an audio file for the instructions.");
        }
    };

    const handleModAudioFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleModAudioFile(file);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const extra = {};
        if (brandName.trim()) extra.brandName = brandName.trim();
        if (audioStyle) extra.audioStyle = audioStyle;
        if (videoStyle) extra.videoStyle = videoStyle;
        if (videoPace)  extra.videoPace  = videoPace;
        if (castMode) extra.castMode = castMode;
        // Persist the rough-idea picker selections onto the project so they
        // survive a reload and so downstream stages (Cast & World,
        // Storyboard, Pre-Production) can read them. Only emit when the
        // user actually picked something — otherwise we'd overwrite an
        // existing initialData value with null on the no-op path.
        if (pickerValue.industryId)            extra.industryId          = pickerValue.industryId;
        if (pickerValue.videoTypeId)           extra.videoTypeId         = pickerValue.videoTypeId;
        if (pickerValue.customVideoTypeName)   extra.customVideoTypeName = pickerValue.customVideoTypeName;
        if (pickerValue.customIndustryName)    extra.customIndustryName  = pickerValue.customIndustryName;
        if (pickerValue.castMode)              extra.castMode            = pickerValue.castMode;
        // Brand scope (Feb 2026): forward saved-brand id + product/service
        // ids so App.js can persist them on activeProject and downstream AI
        // calls (concepts, script, co-pilot) carry the scope.
        if (brandScope.brandId)                     extra.brandId    = brandScope.brandId;
        if (Array.isArray(brandScope.productIds) && brandScope.productIds.length) extra.productIds = brandScope.productIds;
        if (Array.isArray(brandScope.serviceIds) && brandScope.serviceIds.length) extra.serviceIds = brandScope.serviceIds;
        // Phase 2 (Feb 2026) split: "I'm not sure — give me ideas" no longer
        // mounts ConceptGenerator inline. On Continue we signal the parent
        // to route to the dedicated Concept step (Step 3), which is where
        // the brand input + references + concept cards now live.
        if (mode === 'generateConcept') {
            onSubmit({ goToConcept: true, ...extra });
            return;
        }
        if (mode === 'text' && script?.trim()) {
            onSubmit({ script, ...extra });
        } else if (mode === 'audio' && audioFile) {
            onSubmit({ audio: audioFile, ...extra });
        } else if (mode === 'concept' && generationError && concept?.trim()) {
            // If script generation failed but user wants to continue, use concept as script
            onSubmit({ script: concept, ...extra });
        }
    };

    const isSubmitDisabled = (mode === 'text' && !script?.trim()) || 
                             (mode === 'audio' && !audioFile) || 
                             (mode === 'concept' && !generationError && !concept?.trim()) || 
                             mode === 'scratch';

    return (
        <>
            {showStyleModal && (
                <AudioStyleModal 
                    initialVideoStyle={videoStyle}
                    initialVideoPace={videoPace}
                    onConfirm={handleStyleConfirm} 
                    onClose={() => { setShowStyleModal(false); setPendingAction(null); }} 
                />
            )}
            <div className="w-full h-full overflow-y-auto flex items-start justify-center p-6 md:p-8" style={{ background: '#050505', fontFamily: "'Manrope', sans-serif" }}>
                <div className="w-full max-w-3xl">
                    {/* Card */}
                    <div className="rounded-2xl border border-white/10 overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl" style={{ background: 'rgba(15, 17, 21, 0.85)' }}>
                        {/* Header */}
                        <div className="px-8 pt-8 pb-2">
                            <div className="flex items-center gap-3 mb-1">
                                <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                                    <MagicWandIcon className="w-6 h-6 text-violet-400" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">What's your story?</h1>
                                    <p className="text-sm text-zinc-400 mt-0.5">Pick how you'd like to start — we'll guide you through the rest.</p>
                                    {/* Feb 2026 — Show the resolved per-shot
                                        duration so users understand how the
                                        script will be paced. Pulled from the
                                        AI Toolkit's picked video model. */}
                                    {(() => {
                                        const { seconds, source, modelKey } = resolveShotDurationSeconds();
                                        return (
                                            <div
                                                className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-violet-500/10 border border-violet-500/30 text-[11px] text-violet-200"
                                                data-testid="shot-duration-hint"
                                                title={source === 'model'
                                                    ? `Your AI Toolkit video model (${modelKey}) supports up to ${seconds}s clips. Each shot in the script will be paced to fit — no truncation at render time.`
                                                    : `No video model preference found — using a safe ${seconds}s per-shot pace. Pick a video model in AI Toolkit to have this auto-tuned.`}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-violet-400"></span>
                                                Shots will be paced for {seconds}s clips
                                                {source === 'model' && modelKey && (
                                                    <span className="text-violet-400/70 ml-0.5">· matched to {modelKey}</span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        <div className="px-8 pb-8">
                            {/* Brand + Products picker (Feb 2026) — Story
                                page addition. Rendered above Step 1 so it
                                sits at the top of the wizard. Silent-hides
                                when the user has no saved brands. When a
                                brand is applied, we forward the scope on
                                submit and the concept step suppresses its
                                manual brand/reference block. */}
                            <div className="mt-2 mb-6">
                                <BrandScopePicker
                                    value={brandScope}
                                    aspectRatio={initialData.aspectRatio || '16:9'}
                                    onApply={({ brandId, productIds, serviceIds, castAndWorld, brand, pickedProductsFull, pickedServicesFull, productsMissingSheet }) => {
                                        setBrandScope({ brandId, productIds, serviceIds });
                                        if (brand?.name && !brandName.trim()) setBrandName(brand.name);
                                        // Feb 2026 — Auto-populate the local
                                        // Industry picker (used on the "I have
                                        // a rough idea" path) from the brand
                                        // details so the user doesn't have to
                                        // re-pick it. Never overrides an
                                        // industry the user already chose.
                                        setPickerValue((prev) => {
                                            if (prev.industryId) return prev;
                                            const { industry: suggested, confidence } = suggestIndustryFromBrand(brand);
                                            if (!suggested || confidence <= 0) return prev;
                                            return {
                                                ...prev,
                                                industryId: suggested.id,
                                                // clear any stale video-type when the industry changes.
                                                videoTypeId: null,
                                                customVideoTypeName: '',
                                                // Reset custom industry label — we picked a canonical.
                                                customIndustryName: '',
                                            };
                                        });
                                        if (typeof onBrandScopeApply === 'function') {
                                            onBrandScopeApply({ brandId, productIds, serviceIds, castAndWorld, brand, pickedProductsFull, pickedServicesFull, productsMissingSheet });
                                        }
                                    }}
                                    onClear={() => {
                                        setBrandScope({ brandId: null, productIds: [], serviceIds: [] });
                                        if (typeof onBrandScopeApply === 'function') {
                                            onBrandScopeApply({ brandId: null, productIds: [], serviceIds: [], castAndWorld: null, brand: null });
                                        }
                                    }}
                                />
                            </div>
                            {/* Step 1: How would you like to start? */}
                            <div className="mt-4 mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-[11px] font-bold text-violet-300">1</div>
                                    <div className="text-sm font-semibold text-zinc-200">How would you like to start?</div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="story-mode-selector">
                                    {[
                                        {
                                            id: 'generateConcept',
                                            label: "I'm not sure — give me ideas",
                                            desc: 'Best for first-timers. Tell us your brand and we suggest 3 ad concepts you can pick from.',
                                            icon: Sparkles,
                                            recommended: true,
                                        },
                                        {
                                            id: 'concept',
                                            label: 'I have a rough idea',
                                            desc: "Type 1–2 lines about what you want; we'll turn it into a full script for you.",
                                            icon: Lightbulb,
                                            recommended: false,
                                        },
                                        {
                                            id: 'text',
                                            label: 'I already have a script',
                                            desc: 'Paste your finished script and we go straight to production.',
                                            icon: FileText,
                                            recommended: false,
                                        },
                                        {
                                            id: 'audio',
                                            label: "I'll record / upload narration",
                                            desc: 'Speak your story or upload a voice file — we transcribe and turn it into a script.',
                                            icon: Mic,
                                            recommended: false,
                                        },
                                        {
                                            id: 'scratch',
                                            label: 'Start from scratch',
                                            desc: 'Skip the AI script. Jump straight to Cast & World, then build your storyboard scene by scene.',
                                            icon: Pencil,
                                            recommended: false,
                                        },
                                    ].map(m => {
                                        const isActive = mode === m.id;
                                        const Icon = m.icon;
                                        return (
                                            <button
                                                key={m.id}
                                                type="button"
                                                onClick={() => setMode(m.id)}
                                                className={`relative text-left p-4 rounded-xl border transition-all ${
                                                    isActive
                                                        ? 'bg-violet-500/15 border-violet-500/50 ring-2 ring-violet-500/30 shadow-lg shadow-violet-500/10'
                                                        : 'bg-white/[0.03] border-white/10 hover:border-white/25 hover:bg-white/[0.06]'
                                                }`}
                                                data-testid={`mode-${m.id}`}
                                            >
                                                {m.recommended && !isActive && (
                                                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-bold">
                                                        Recommended
                                                    </span>
                                                )}
                                                {isActive && (
                                                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center">
                                                        <CheckCircle className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                )}
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                        isActive ? 'bg-violet-500/30 border border-violet-400/50' : 'bg-white/5 border border-white/10'
                                                    }`}>
                                                        <Icon className={`w-5 h-5 ${isActive ? 'text-violet-200' : 'text-zinc-300'}`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0 pr-6">
                                                        <div className={`text-base font-semibold ${isActive ? 'text-white' : 'text-zinc-100'}`}>
                                                            {m.label}
                                                        </div>
                                                        <div className="text-sm text-zinc-400 mt-1 leading-relaxed">
                                                            {m.desc}
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Step 2: Tell us about your story */}
                            <div className="flex items-center gap-2 mb-3 mt-2">
                                <div className="w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-[11px] font-bold text-violet-300">2</div>
                                <div className="text-sm font-semibold text-zinc-200">
                                    {mode === 'generateConcept' && 'Generate concepts for your video'}
                                    {mode === 'concept'         && 'Tell us your idea'}
                                    {mode === 'text'            && 'Paste your script'}
                                    {mode === 'audio'           && 'Add your narration'}
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {mode === 'text' && (
                                    <div>
                                        <textarea
                                            value={script}
                                            onChange={(e) => setScript(e.target.value)}
                                            placeholder="Example: A detective in a rain-soaked city chases a phantom that only appears in reflections..."
                                            className="w-full h-56 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-base text-white placeholder-zinc-600 resize-none focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all leading-relaxed"
                                        />
                                        <p className="text-sm text-zinc-500 mt-2 flex items-center gap-1.5">
                                            <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                                            Tip: Include scene-by-scene details and any dialogue you want spoken.
                                        </p>
                                    </div>
                                )}
                                {mode === 'concept' && (
                                    <div className="space-y-6">
                                        <div>
                                            <textarea
                                                value={concept}
                                                onChange={(e) => setConcept(e.target.value)}
                                                placeholder="Example: A lonely astronaut on Mars discovers they are not alone."
                                                className="w-full h-40 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-base text-white placeholder-zinc-600 resize-none focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all leading-relaxed"
                                                disabled={isGenerating}
                                                data-testid="rough-idea-textarea"
                                            />
                                            <p className="text-sm text-zinc-500 mt-2 flex items-center gap-1.5">
                                                <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                                                Tip: One or two sentences is enough. We'll fill in the rest.
                                            </p>
                                        </div>

                                        {/* Industry / Video Type / Cast picker — same as the
                                            "I'm not sure" path but for the rough-idea flow.
                                            stepOffset starts at 3 to match the "Tell us your
                                            idea" being step 2 of the rough-idea wizard. */}
                                        <IndustryVideoTypeCastPicker
                                            value={pickerValue}
                                            onChange={setPickerValue}
                                            stepOffset={3}
                                        />

                                        {generationError && <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-xl text-xs">{generationError}</div>}
                                        <button
                                            type="button"
                                            onClick={triggerGenerateScript}
                                            disabled={
                                                !concept?.trim()
                                                || isGenerating
                                                // Require Industry + Video-Type for a more
                                                // targeted script generation. Cast defaults
                                                // to 'with_actors' so it's never blocking.
                                                || !pickerValue.industryId
                                                || !pickerValue.videoTypeId
                                                || (pickerValue.videoTypeId === 'other-custom' && !pickerValue.customVideoTypeName?.trim())
                                                // Also require the typed industry label
                                                // when the user picks "Other" for industry.
                                                || (pickerValue.industryId === 'other-industry' && !pickerValue.customIndustryName?.trim())
                                            }
                                            className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full transition-all text-sm"
                                            data-testid="rough-idea-generate-script-btn"
                                        >
                                            {isGenerating ? <LoadingSpinner className="w-4 h-4" /> : <MagicWandIcon className="w-4 h-4" />}
                                            {isGenerating ? 'Generating Script...' : 'Generate Script'}
                                        </button>
                                    </div>
                                )}
                                {mode === 'generateConcept' && (
                                    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 space-y-3" data-testid="generate-concept-info">
                                        <div className="flex items-center gap-2">
                                            <Sparkles className="w-4 h-4 text-violet-400" />
                                            <span className="text-sm font-semibold text-white">We&apos;ll help you brainstorm</span>
                                        </div>
                                        <p className="text-sm text-zinc-400 leading-relaxed">
                                            Click <span className="text-violet-300 font-semibold">Continue</span> to go to the Concept step.
                                            There you&apos;ll add your brand name, optionally drop in a website / social handle / reference video,
                                            and we&apos;ll suggest 3 tailored video ideas you can pick from.
                                        </p>
                                    </div>
                                )}
                                {mode === 'audio' && (
                                    <div>
                                        {audioFile ? (
                                            <div className="space-y-4">
                                                <div className="p-4 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between">
                                                    <p className="text-zinc-300 text-sm truncate">{audioFile.name}</p>
                                                    <button type="button" onClick={() => setAudioFile(null)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors">
                                                        <DeleteIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                {generationError && <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-xl text-xs">{generationError}</div>}
                                                <button
                                                    type="button"
                                                    onClick={triggerGenerateScriptFromAudio}
                                                    disabled={isGenerating}
                                                    className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full transition-all text-sm"
                                                >
                                                    {isGenerating ? <LoadingSpinner className="w-4 h-4" /> : <MagicWandIcon className="w-4 h-4" />}
                                                    {isGenerating ? 'Generating Script...' : 'Generate Script from Audio'}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                                                <div className="flex border-b border-white/10">
                                                    <button type="button" onClick={() => setAudioMode('upload')}
                                                        className={`flex-1 py-2.5 text-center text-xs font-semibold transition-colors ${audioMode === 'upload' ? 'bg-violet-500/10 text-violet-400' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`}>
                                                        Upload File
                                                    </button>
                                                    <button type="button" onClick={() => setAudioMode('record')}
                                                        className={`flex-1 py-2.5 text-center text-xs font-semibold transition-colors ${audioMode === 'record' ? 'bg-violet-500/10 text-violet-400' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`}>
                                                        Record Audio
                                                    </button>
                                                </div>
                                                <div>
                                                    {audioMode === 'upload' ? (
                                                        <>
                                                            <input type="file" accept="audio/*" ref={audioFileInputRef} onChange={handleAudioFileChange} className="hidden" />
                                                            <div
                                                                onDrop={(e) => handleDrop(e, handleAudioFile)}
                                                                onDragOver={handleDragOver}
                                                                onDragEnter={handleDragEnter}
                                                                onDragLeave={handleDragLeave}
                                                                onClick={() => audioFileInputRef.current?.click()}
                                                                className={`w-full h-48 p-3 flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? 'bg-violet-500/10' : 'hover:bg-white/5'}`}
                                                            >
                                                                <UploadIcon className="w-10 h-10 text-zinc-600 mb-2" />
                                                                <p className="text-zinc-400 text-sm">Drag & drop your audio file here</p>
                                                                <p className="text-zinc-600 text-xs mt-1">or click to browse</p>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <AudioRecorder onRecordingComplete={setAudioFile} />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {mode === 'scratch' && (
                                    <div className="text-center py-10 px-6 bg-white/5 border border-white/10 rounded-xl">
                                        <h3 className="text-lg font-bold text-zinc-200 mb-2">Build Your Story Manually</h3>
                                        <p className="text-zinc-500 text-sm max-w-md mx-auto">
                                            Skip AI script analysis. Move directly to creating characters and building your storyboard scene by scene.
                                        </p>
                                    </div>
                                )}
                                
                                {(generationError || externalError) && mode !== 'concept' && (
                                    <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-xl text-xs">
                                        {externalError || generationError}
                                    </div>
                                )}
                                
                                <div className="flex items-center gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={onBack}
                                        className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-full px-6 py-3 text-sm font-semibold transition-all"
                                    >
                                        <ArrowLeftIcon className="w-4 h-4" />
                                        Back
                                    </button>
                                    {mode === 'scratch' ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const extra = {};
                                                if (brandName.trim()) extra.brandName = brandName.trim();
                                                if (audioStyle) extra.audioStyle = audioStyle;
                                                if (videoStyle) extra.videoStyle = videoStyle;
                                                if (videoPace)  extra.videoPace  = videoPace;
                                                if (castMode) extra.castMode = castMode;
                                                onSubmit({ script: '', ...extra });
                                            }}
                                            className="flex-1 flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold py-3 rounded-full transition-all hover:-translate-y-0.5 text-sm"
                                        >
                                            Continue to Characters
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            disabled={isSubmitDisabled}
                                            className="flex-1 flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full transition-all hover:-translate-y-0.5 text-sm"
                                            data-testid="story-continue-btn"
                                        >
                                            {mode === 'generateConcept' ? 'Continue to Concept' : 'Continue to Characters'}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
